import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { MatDialog } from "@angular/material";
import { NotificationToastService } from "../../../services/notification-toast.service";
import { MenuService } from "../../../services/menu.service";
import { ReservationService } from "../../../services/reservation.service";
import { PagerControlComponent } from "../../controls/pager/pager-control.component";
import { SearchCompleteFilterComponent } from "../../controls/search-complete-filter/search-complete-filter.component";
import { ReservationHisRequest } from "../../../model/reservation-his-request";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ReservationHisRequestDialogComponent } from "../../dialogs/reservartion-his-request-dialog/reservartion-his-request-dialog.component";
import { IFilterResponse, SearchFilter } from "../../../infrastructure/iFilter-component";
import { SearchFilterComponent } from "../../search-filter/search-filter.component";
import { ReservationHisFilterComponent } from "../reservation-his-filter/reservation-his-filter.component";
import { Router } from "@angular/router";

@Component({
  selector: "app-reservation-his-search-component",
  templateUrl: "./reservation-his-search.component.html",
  styleUrls: ["./reservation-his-search.component.css"],
})
export class ReservationHisSearchComponent implements OnInit, OnDestroy {
  private readonly sessionColumnsKey = "reservation-his-search.columns.v1";
  private readonly sessionSortKey = "reservation-his-search.sort";
  private readonly sessionColumnWidthsKey = "reservation-his-search.column-widths.v1";
  private hasStoredColumnsState = false;

  loading = false;
  form: FormGroup;
  searchFilters: SearchFilter[] = [];
  draggedColumn: string | null = null;
  sortActive = "";
  sortDirection = "";
  allItems: ReservationHisRequest[] = [];
  columnFilters: { [key: string]: string } = {};
  columnWidths: { [key: string]: number } = {};
  displayedColumns: string[] = [
    "Date",
    "DocumentType",
    "Document",
    "Status",
    "PatientFullName",
    "Location",
    "PhysicianFullName",
    "star",
  ];
  dataSource: ReservationHisRequest[] = [];

  readonly columnsDefinition: Array<{ field: string; title: string }> = [
    { field: "Date", title: "Fecha" },
    { field: "DocumentType", title: "Tipo de Documento" },
    { field: "Document", title: "Documento" },
    { field: "Status", title: "Estado" },
    { field: "PatientFullName", title: "Paciente" },
    { field: "Location", title: "Ubicación" },
    { field: "PhysicianFullName", title: "Profesional" }
  ];

  @ViewChild("searchFilter", { static: false })
  searchFilter: SearchFilterComponent;
  @ViewChild("pager", { static: true }) pager: PagerControlComponent;
  @ViewChild("filters", { static: false }) filters: ReservationHisFilterComponent;

  constructor(
    public dialog: MatDialog,
    private menuService: MenuService,
    private notificationToastService: NotificationToastService,
    private reservationService: ReservationService,
    private formBuilder: FormBuilder,
    private router: Router
  ) {
    this.menuService.setMenuTitle("Turnos", "Solicitudes HIS");
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.form = this.formBuilder.group({
      finished: [false],
      patientId: [null],
      date: [today],
    });
  }

  ngOnInit(): void {
    this.restoreColumnsOrder();
    this.normalizeDisplayedColumns();
    this.restoreSortState();
    this.restoreColumnWidths();
  }

  ngOnDestroy() {
    this.removeResizeListeners();
  }

  get availableColumns(): Array<{ field: string; title: string }> {
    return this.columnsDefinition;
  }

  get orderedColumnsDefinition(): Array<{ field: string; title: string }> {
    const order = this.displayedColumns.filter((column) => column !== "star");
    const visibleColumns = new Set(order);
    const orderMap = new Map<string, number>(
      order.map((column, index) => [column, index])
    );

    return this.columnsDefinition
      .filter((column) => visibleColumns.has(column.field))
      .sort((a, b) => {
        const aIndex = orderMap.has(a.field) ? orderMap.get(a.field) : 999;
        const bIndex = orderMap.has(b.field) ? orderMap.get(b.field) : 999;
        return aIndex - bIndex;
      });
  }

  isColumnVisible(column: string): boolean {
    return this.displayedColumns.indexOf(column) >= 0;
  }

  setColumnVisibility(column: string, visible: boolean) {
    const dataColumns = this.displayedColumns.filter((item) => item !== "star");
    if (visible) {
      if (dataColumns.indexOf(column) < 0) {
        dataColumns.push(column);
      }
    } else {
      if (dataColumns.length <= 1) {
        return;
      }
      const removeIndex = dataColumns.indexOf(column);
      if (removeIndex >= 0) {
        dataColumns.splice(removeIndex, 1);
      }
      if (this.sortActive === column) {
        this.sortActive = "";
        this.sortDirection = "";
        sessionStorage.removeItem(this.sessionSortKey);
      }
      if (this.columnFilters[column]) {
        delete this.columnFilters[column];
      }
    }
    this.displayedColumns = [...dataColumns, "star"];
    this.saveColumnsOrder();
    this.refreshGrid();
  }

  showAllColumns() {
    const allColumns = this.availableColumns.map((column) => column.field);
    this.displayedColumns = [...allColumns, "star"];
    this.saveColumnsOrder();
    this.refreshGrid();
  }

  sortByColumn(column: string) {
    if (this.sortActive !== column) {
      this.sortAllData({ active: column, direction: "asc" });
      return;
    }
    if (this.sortDirection === "asc") {
      this.sortAllData({ active: column, direction: "desc" });
      return;
    }
    this.sortAllData({ active: "", direction: "" });
  }

  getSortIcon(column: string): string {
    if (this.sortActive !== column) {
      return "unfold_more";
    }
    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  sortAllData(sortEvent: any) {
    if (!sortEvent || !sortEvent.active || sortEvent.direction === "") {
      this.sortActive = "";
      this.sortDirection = "";
      sessionStorage.removeItem(this.sessionSortKey);
      this.refreshGrid();
      return;
    }
    this.sortActive = sortEvent.active;
    this.sortDirection = sortEvent.direction;
    this.saveSortState();
    this.refreshGrid();
  }

  onColumnFilterInput(column: string, value: string) {
    this.columnFilters[column] = value;
    this.refreshGrid();
  }

  getColumnWidth(column: string): number | null {
    const width = this.columnWidths[column];
    return width && width > 0 ? width : null;
  }

  startResize(event: MouseEvent, column: string) {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const headerCell = (event.target as HTMLElement).closest("th");
    const measuredWidth = headerCell ? Math.round(headerCell.getBoundingClientRect().width) : 100;
    const startWidth = this.getColumnWidth(column) || Math.max(80, measuredWidth);
    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = Math.max(80, startWidth + delta);
      this.columnWidths = { ...this.columnWidths, [column]: nextWidth };
    };
    const onMouseUp = () => {
      this.removeResizeListeners();
      this.saveColumnWidths();
    };
    (window as any).__reservationHisSearchResizeMove = onMouseMove;
    (window as any).__reservationHisSearchResizeUp = onMouseUp;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  onHeaderDragStart(event: DragEvent, column: string) {
    this.draggedColumn = column;
    if (event.dataTransfer) {
      event.dataTransfer.setData("text/plain", column);
      event.dataTransfer.effectAllowed = "move";
    }
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  onHeaderDrop(event: DragEvent, targetColumn: string) {
    event.preventDefault();
    if (!this.draggedColumn || this.draggedColumn === targetColumn) {
      return;
    }
    const dataColumns = this.displayedColumns.filter((column) => column !== "star");
    const sourceIndex = dataColumns.indexOf(this.draggedColumn);
    const targetIndex = dataColumns.indexOf(targetColumn);
    if (sourceIndex < 0 || targetIndex < 0) {
      this.draggedColumn = null;
      return;
    }
    moveItemInArray(dataColumns, sourceIndex, targetIndex);
    this.displayedColumns = [...dataColumns, "star"];
    this.saveColumnsOrder();
    this.draggedColumn = null;
  }

  onHeaderDragEnd() {
    this.draggedColumn = null;
  }

  async search() {
    try {
      this.loading = true;
      const values = this.form.getRawValue();
      const items = await this.reservationService.getReservationHis(
        values.finished,
        values.patientId,
        values.date
      );
      this.allItems = items || [];
      this.refreshGrid();
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error || err), "Solicitudes HIS");
    } finally {
      this.loading = false;
    }
  }

  onStartSearch() {
    this.loading = true;
  }

  onEndSearch(response: IFilterResponse<ReservationHisRequest>) {
    this.searchFilters = response && response.searchFilters ? response.searchFilters : [];
    this.allItems = response && response.items ? [...response.items] : [];
    this.refreshGrid();
    this.loading = false;
  }

  applyFilter() {
    // no-op, kept for compatibility with the shared search-filter pattern
  }

  selectedPageIndexChange(pagedItems: any[]) {
    this.dataSource = pagedItems ? [...pagedItems] : [];
  }

  openDetail(id: number) {
    this.dialog.open(ReservationHisRequestDialogComponent, {
      width: "70%",
      maxWidth: "800px",
      data: id,
    });
  }

  reserve(reservationhisId: number) {
    this.router.navigate(["/reservation-extraordinary-wizard", reservationhisId]);
  }

  async finish(id: number) {
    await this.reservationService.finalizeReservationHis(id);
    this.notificationToastService.success("Solicitud finalizada correctamente", "Solicitudes HIS");
    await this.search();
  }

  async revert(id: number) {
    await this.reservationService.rollBackReservationHis(id);
    this.notificationToastService.success("Solicitud revertida correctamente", "Solicitudes HIS");
    await this.search();
  }

  private getSortableValue(item: ReservationHisRequest, property: string): any {
    const value = (item as any)[property];
    if (value === null || value === undefined) {
      return "";
    }
    return typeof value === "string" ? value.toLocaleLowerCase() : value;
  }

  private saveColumnsOrder() {
    sessionStorage.setItem(this.sessionColumnsKey, JSON.stringify(this.displayedColumns));
  }

  private restoreColumnsOrder() {
    const stored = sessionStorage.getItem(this.sessionColumnsKey);
    if (!stored) {
      return;
    }
    try {
      const parsed: string[] = JSON.parse(stored);
      if (!parsed || !Array.isArray(parsed)) {
        return;
      }
      const allowed = new Set(this.availableColumns.map((column) => column.field));
      const parsedDataColumns = parsed
        .filter((column) => column !== "star")
        .filter((column, index, array) => array.indexOf(column) === index)
        .filter((column) => allowed.has(column));
      if (parsedDataColumns.length > 0) {
        this.displayedColumns = [...parsedDataColumns, "star"];
        this.hasStoredColumnsState = true;
      }
    } catch {
      sessionStorage.removeItem(this.sessionColumnsKey);
    }
  }

  private normalizeDisplayedColumns() {
    const expectedDataColumns = this.availableColumns.map((column) => column.field);
    const expectedSet = new Set(expectedDataColumns);
    const currentDataColumns = this.displayedColumns
      .filter((column) => column !== "star")
      .filter((column) => expectedSet.has(column));
    let normalizedDataColumns = [...currentDataColumns];
    if (!this.hasStoredColumnsState) {
      normalizedDataColumns = [...expectedDataColumns];
    }
    if (normalizedDataColumns.length === 0) {
      normalizedDataColumns = [...expectedDataColumns];
    }
    this.displayedColumns = [...normalizedDataColumns, "star"];
  }

  private saveSortState() {
    sessionStorage.setItem(this.sessionSortKey, JSON.stringify({ active: this.sortActive, direction: this.sortDirection }));
  }

  private restoreSortState() {
    const stored = sessionStorage.getItem(this.sessionSortKey);
    if (!stored) {
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      if (parsed && parsed.active && parsed.direction) {
        this.sortActive = parsed.active;
        this.sortDirection = parsed.direction;
      }
    } catch {
      sessionStorage.removeItem(this.sessionSortKey);
    }
  }

  private saveColumnWidths() {
    sessionStorage.setItem(this.sessionColumnWidthsKey, JSON.stringify(this.columnWidths));
  }

  private restoreColumnWidths() {
    const stored = sessionStorage.getItem(this.sessionColumnWidthsKey);
    if (!stored) {
      this.columnWidths = {};
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== "object") {
        return;
      }
      const validColumns = new Set(this.columnsDefinition.map((column) => column.field));
      const restored: { [key: string]: number } = {};
      Object.keys(parsed).forEach((column) => {
        if (!validColumns.has(column)) {
          return;
        }
        const width = Number(parsed[column]);
        if (!isNaN(width) && width >= 80 && width <= 500) {
          restored[column] = width;
        }
      });
      this.columnWidths = restored;
    } catch {
      sessionStorage.removeItem(this.sessionColumnWidthsKey);
      this.columnWidths = {};
    }
  }

  private refreshGrid() {
    const currentPage = this.pager && this.pager.pager && this.pager.pager.currentPage ? this.pager.pager.currentPage : 1;
    let items = this.applyColumnFilters(this.allItems || []);
    if (this.sortActive && this.sortDirection) {
      items = this.sortItems([...items], this.sortActive, this.sortDirection);
    }
    this.pager.setItems(items);
    if (currentPage > 1) {
      this.pager.setPage(currentPage);
    }
  }

  private applyColumnFilters(items: ReservationHisRequest[]): ReservationHisRequest[] {
    const filters = Object.keys(this.columnFilters || {}).filter((key) => !!this.columnFilters[key] && this.columnFilters[key].trim() !== "");
    if (filters.length === 0) {
      return [...items];
    }
    return items.filter((item) =>
      filters.every((column) => {
        const filterValue = this.normalizeFilterValue(this.columnFilters[column]);
        const rawValue = (item as any)[column];
        const itemValue = this.normalizeFilterValue(String(rawValue != null ? rawValue : ""));
        return itemValue.indexOf(filterValue) >= 0;
      })
    );
  }

  private sortItems(items: ReservationHisRequest[], active: string, direction: string): ReservationHisRequest[] {
    return items.sort((a, b) => {
      const aValue = this.getSortableValue(a, active);
      const bValue = this.getSortableValue(b, active);
      if (aValue < bValue) {
        return direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }

  private normalizeFilterValue(value: string): string {
    const normalized = value ? String(value) : "";
    return normalized.toLocaleLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  private removeResizeListeners() {
    const onMouseMove = (window as any).__reservationHisSearchResizeMove;
    const onMouseUp = (window as any).__reservationHisSearchResizeUp;
    if (onMouseMove) {
      document.removeEventListener("mousemove", onMouseMove);
      (window as any).__reservationHisSearchResizeMove = null;
    }
    if (onMouseUp) {
      document.removeEventListener("mouseup", onMouseUp);
      (window as any).__reservationHisSearchResizeUp = null;
    }
  }
}
